import OpenAI from "openai";

import {
  AIProvider,
  AIRequest,
  AIResponse,
  AIStreamChunk,
  AIError,
} from "./aiProvider";
import { openaiCircuit, CircuitOpenError } from "../../runtime/httpCircuitBreaker";

const API_KEY = process.env.VITE_OPENAI_API_KEY || process.env.OPENAI_API_KEY;
const DEFAULT_OPENAI_MODEL = (process.env.NUVRA_OPENAI_DEFAULT_MODEL || "gpt-5.6-luna").trim();

// Lazy singleton - SDK constructor throws when apiKey is empty, so we defer
// instantiation until the first actual call. isAvailable() guards all call sites.
let _client: OpenAI | null = null;
function getClient(): OpenAI {
  if (!_client) {
    _client = new OpenAI({ apiKey: API_KEY! });
  }
  return _client;
}

/** User message content. With images (vision), emit a text part plus image_url
 *  data-URL parts; otherwise a plain string (unchanged). Exported for tests. */
export function openaiUserContent(req: AIRequest): any {
  if (!req.images || req.images.length === 0) return req.prompt;
  return [
    { type: "text", text: req.prompt },
    ...req.images.map(img => ({ type: "image_url", image_url: { url: `data:${img.mediaType};base64,${img.dataBase64}` } })),
  ];
}

export const openaiProvider: AIProvider = {
  name: "openai",

  capabilities: {
    streaming: true,
    tools: true,
    jsonMode: true,
  },

  /**
   🔥 FIX: correct logic
  */
  isAvailable() {
    return !!API_KEY;
  },

  async generate(req: AIRequest): Promise<AIResponse> {
    const start = Date.now();

    // Configuration guard - missing API key is not a transient service failure.
    // Checked before circuit.execute() so static config errors never trip the circuit.
    if (!API_KEY) {
      return {
        files: [],
        error: {
          message: "Missing OpenAI API key - provider not configured",
          provider: "openai",
          retryable: false,
        },
      };
    }

    try {
      // FAILURE CLASSIFICATION: fail-closed
      //   No fallback provided. Circuit open = OpenAI unavailable.
      //   CircuitOpenError propagates to catch below → AIError { retryable: false }.
      //   Callers (e.g., aiRouter) may implement cross-provider fallback independently.
      //   Fabricated success responses are FORBIDDEN.
      return await openaiCircuit.execute(async () => {
        const completion = await getClient().chat.completions.create({
          model: req.model || DEFAULT_OPENAI_MODEL,
          temperature: req.temperature ?? 0.7,
          max_tokens: req.maxTokens,
          response_format: { type: "json_object" },
          messages: [
            {
              role: "system",
              content:
                req.systemPrompt ||
                "You are a strict code generator. Return ONLY valid JSON: { files: [{ name, content }] }",
            },
            {
              role: "user",
              content: openaiUserContent(req),
            },
          ],
          ...(req.signal ? { signal: req.signal } : {}),
        });

        const text = completion.choices[0]?.message?.content || "{}";

        let parsed: any = {};
        try {
          parsed = JSON.parse(text);
        } catch {
          // safe fallback
        }

        return {
          files: parsed.files || [],
          raw: text,
          usage: completion.usage
            ? {
                promptTokens: completion.usage.prompt_tokens,
                completionTokens: completion.usage.completion_tokens,
                totalTokens: completion.usage.total_tokens,
              }
            : undefined,
          meta: {
            provider: "openai",
            model: completion.model,
            latencyMs: Date.now() - start,
          },
        };
      });
    } catch (err: any) {
      const error: AIError = {
        message: err?.message || "OpenAI generation failed",
        provider: "openai",
        // CircuitOpenError = do not retry immediately; circuit must recover first.
        // All other errors (5xx, network, timeout) are retryable transient failures.
        retryable: !(err instanceof CircuitOpenError),
      };
      return { files: [], error };
    }
  },

  /**
   🔥 STREAMING (UPGRADED)
  */
  async stream(
    req: AIRequest,
    onChunk: (chunk: AIStreamChunk) => void
  ): Promise<void> {
    const start = Date.now();
    let accumulated = "";

    try {
      /**
       🔥 HARD FAIL IF KEY MISSING
      */
      if (!API_KEY) {
        throw new Error("Missing OpenAI API key");
      }

      const stream = await getClient().chat.completions.create({
        model: req.model || "gpt-4o-mini",
        temperature: req.temperature ?? 0.7,
        max_tokens: req.maxTokens,
        stream: true,

        messages: [
          {
            role: "system",
            content:
              req.systemPrompt ||
              "You are a strict code generator. Return ONLY valid JSON: { files: [{ name, content }] }",
          },
          {
            role: "user",
            content: openaiUserContent(req),
          },
        ],
      });

      for await (const part of stream) {
        const delta = part.choices?.[0]?.delta?.content;

        if (delta) {
          accumulated += delta;

          onChunk({
            content: delta,
          });
        }
      }

      /**
       🔥 FINAL PARSE ATTEMPT
      */
      let parsed: any = {};
      try {
        parsed = JSON.parse(accumulated);
      } catch {
        // safe fallback
      }

      onChunk({
        content: accumulated,
        done: true,
        usage: undefined,
      });
    } catch (err: any) {
      const error: AIError = {
        message: err?.message || "Streaming failed",
        provider: "openai",
        retryable: true,
      };

      onChunk({
        error,
        done: true,
      });
    }
  },
};